import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { postFetcher } from '@/hooks'

// 产品缩写到 pid 的映射
const PAM_APP_ABBREVIATIONS = {
  'T': '1',       // Text Sticker
  'P': '2',       // Personal Sticker
  'HD': '3',      // HD Sticker
  'AS': '4',      // Anim Sticker
  'SK': '5',      // WA Sticker (Maker)
  '2022': '6',    // 2023 Sticker
  'TG': '7',      // TG Sticker
  'STT': '9',     // StickTok
  'NA': '11',     // Neku Android
  'NI': '12',     // Neku iOS
  '2023HD': '13', // HD 2023
  'wonderful': '14-wonderful', // JPSingle Wonderful
  'samoyed': '14-samoyed', // JPSingle Samoyed
  'worker': '14-worker', // JPSingle Worker
  'takoyaki': '14-takoyaki', // JPSingle Takoyaki
  'sushicat': '14-sushicat', // JPSingle Sushicat
  'detectivedog': '14-detectivedog', // JPSingle Detectivedog
  'tanuki': '14-tanuki', // JPSingle Tanuki
  'summercat': '14-summercat', // JPSingle Summercat
  'springbunny': '14-springbunny', // JPSingle Springbunny
  'mamekoslife': '14-mamekoslife', // JPSingle Mamekoslife
  'hamstercookie': '14-hamstercookie', // JPSingle Hamstercookie
  'calicocatdiary': '14-calicocatdiary', // JPSingle Calicocatdiary
  'guineapig': '14-guineapig', // JPSingle Guineapig
  'blondgirl': '14-blondgirl', // JPSingle Blondgirl
  'panda': '14-panda', // JPSingle Panda
  'penguin': '14-penguin', // JPSingle Penguin
  'birthday': '14-birthday', // JPSingle Birthday
  'newyear': '14-newyear', // JPSingle Newyear
  'newyear2': '14-newyear2', // JPSingle Newyear2
  'krnewyear': '14-krnewyear', // JPSingle Krnewyear
  'krnewyear2': '14-krnewyear2', // JPSingle Krnewyear2
  'krgreetings': '14-krgreetings', // JPSingle Krgreetings
  'krpurplegirl': '14-krpurplegirl', // JPSingle Krpurplegirl
  'largefont': '14-largefont', // JPSingle Largefont
  'stickerland': '14-stickerland', // JPSingle Stickerland
  stickfigure: '14-stickfigure',
  'OC': '16',     // Neku OC
  'G': '17',      // Gemoji
}

// 解析 CSV 为 JSON
function parseCsvLine(line) {
  const values = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      inQuotes = !inQuotes
      continue
    }
    if (char === ',' && !inQuotes) {
      values.push(current.trim())
      current = ''
      continue
    }
    current += char
  }
  values.push(current.trim())
  return values
}

function parseCsvToJson(csvText) {
  const lines = csvText.trim().split('\n')
  if (lines.length < 2) {
    throw new Error('CSV 数据格式不正确')
  }

  return lines.slice(1).map((line, index) => {
    const values = parseCsvLine(line)
    return {
      rowIndex: index + 2,
      platform: values[0] || '',
      adFormat: values[1] || '',
      productPrefix: values[2] || '',
      adType: values[3] || '',
      placement: values[4] || '',
      country: values[5] || null,
      pamVariant: values[6] || '',
      pangleAppId: values[7] || null,
      pangleUnitId: values[8] || null,
      applovinKey: values[9] || null,
      vungleAppId: values[10] || null,
      vungleUnitId: values[11] || null,
      mtgKey: values[12] || null,
      mtgAppId: values[13] || null,
      mtgPlacementId: values[14] || null,
      mtgUnitId: values[15] || null,
      meta: values[16] || null,
      waterfallPrices: values[17]
        ? values[17]
            .split(',')
            .map((p) => parseFloat(p.trim()))
            .filter((p) => !isNaN(p) && p > 0)
        : [],
    }
  })
}

// 验证单行数据
function validateRow(row) {
  const errors = []

  if (!row.platform) errors.push('平台不能为空')
  if (!['ANDROID', 'IOS', 'Android', 'iOS'].includes(row.platform)) {
    errors.push('平台必须是 ANDROID 或 IOS')
  }
  if (!row.adFormat) errors.push('格式不能为空')
  if (!row.productPrefix) errors.push('产品不能为空')
  if (!row.adType) errors.push('类型不能为空')
  if (!row.placement) errors.push('版位不能为空')
  if (!row.waterfallPrices || row.waterfallPrices.length === 0) {
    errors.push('Waterfall 价格不能为空')
  }

  if (row.productPrefix && !PAM_APP_ABBREVIATIONS[row.productPrefix]) {
    errors.push(`产品 '${row.productPrefix}' 配置不存在，请检查产品缩写`)
  }

  const hasBidding =
    row.pangleAppId ||
    row.applovinKey ||
    row.vungleAppId ||
    row.mtgKey ||
    row.meta
  if (!hasBidding) {
    errors.push('至少需要配置一个 Bidding 广告网络')
  }

  return errors
}

// 生成名称预览
function generateNamePreview(row) {
  const now = new Date()
  const date = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
  
  const adUnitName = [row.productPrefix, row.placement, row.adType, date]
    .filter(p => p && p.trim())
    .join('-')
  
  const mgParts = [row.productPrefix, row.adType]
  if (row.country) mgParts.push(row.country.toUpperCase())
  if (row.pamVariant) mgParts.push(row.pamVariant)
  const mediationGroupName = mgParts.filter(p => p && p.trim()).join('-')
  
  return { adUnitName, mediationGroupName }
}

function formatBiddingConfig(row) {
  const configs = []

  if (row.pangleAppId || row.pangleUnitId) {
    configs.push(`Pangle(app:${row.pangleAppId || '-'}, unit:${row.pangleUnitId || '-'})`)
  }
  if (row.applovinKey) {
    configs.push(`AppLovin(key:${row.applovinKey})`)
  }
  if (row.vungleAppId || row.vungleUnitId) {
    configs.push(`Vungle(app:${row.vungleAppId || '-'}, unit:${row.vungleUnitId || '-'})`)
  }
  if (row.mtgKey || row.mtgAppId || row.mtgPlacementId || row.mtgUnitId) {
    configs.push(
      `Mintegral(key:${row.mtgKey || '-'}, app:${row.mtgAppId || '-'}, placement:${row.mtgPlacementId || '-'}, unit:${row.mtgUnitId || '-'})`,
    )
  }
  if (row.meta) {
    configs.push(`Meta(app:${row.meta})`)
  }

  return configs.length > 0 ? configs.join('\n') : '-'
}

function PamCreatePage() {
  const [csvText, setCsvText] = useState('')
  const [parsedData, setParsedData] = useState(null)
  const [errors, setErrors] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [inputCode, setInputCode] = useState('')
  const [verifyError, setVerifyError] = useState('')

  const handleParse = () => {
    try {
      const parsed = parseCsvToJson(csvText)
      const validationErrors = []

      parsed.forEach((row) => {
        const rowErrors = validateRow(row)
        if (rowErrors.length > 0) {
          validationErrors.push({
            rowIndex: row.rowIndex,
            errors: rowErrors,
          })
        }
      })

      setParsedData(parsed)
      setErrors(validationErrors)
      setResult(null)
    } catch (error) {
      setErrors([{ rowIndex: 0, errors: [error.message] }])
      setParsedData(null)
    }
  }

  const handleCreate = () => {
    // 生成 6 位随机验证码
    const code = Math.floor(100000 + Math.random() * 900000).toString()
    setVerificationCode(code)
    setInputCode('')
    setVerifyError('')
    setShowConfirmDialog(true)
  }

  const handleConfirmCreate = async () => {
    if (inputCode !== verificationCode) {
      setVerifyError('验证码不匹配，请重试')
      return
    }

    setIsLoading(true)

    try {
      // 获取第一行的 pid
      const firstRow = parsedData[0]
      const pid = PAM_APP_ABBREVIATIONS[firstRow.productPrefix]

      const response = await postFetcher(
        `/admob/pam/full-create?pid=${pid}`,
        { arg: { rows: parsedData } },
      )

      setResult(response)
    } catch (error) {
      setResult({
        success: false,
        message: error.message || '创建失败',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const hasErrors = errors.length > 0
  const canCreate = parsedData && parsedData.length > 0 && !hasErrors

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>PAM 全流程创建</CardTitle>
          <CardDescription>
            从 CSV 创建 Ad Unit、中介组、Bidding 和 Waterfall 配置
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              CSV 数据输入
            </label>
            <Textarea
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder="粘贴 CSV 数据..."
              className="min-h-[200px] font-mono text-sm"
            />
          </div>
          <Button onClick={handleParse} disabled={!csvText.trim()}>
            解析数据
          </Button>
        </CardContent>
      </Card>

      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            <div className="font-semibold mb-2">数据验证失败：</div>
            <ul className="list-disc list-inside space-y-1">
              {errors.map((error, idx) => (
                <li key={idx}>
                  第{error.rowIndex}行: {error.errors.join('; ')}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {parsedData && !hasErrors && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>数据预览</CardTitle>
              <CardDescription>
                共 {parsedData.length} 行数据
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">序号</th>
                      <th className="text-left p-2">平台</th>
                      <th className="text-left p-2">格式</th>
                      <th className="text-left p-2">产品</th>
                      <th className="text-left p-2">类型</th>
                      <th className="text-left p-2">版位</th>
                      <th className="text-left p-2">国家</th>
                      <th className="text-left p-2">PAM组别</th>
                      <th className="text-left p-2">Waterfall 价格</th>
                      <th className="text-left p-2">Bidding 配置</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 10).map((row, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="p-2">{idx + 1}</td>
                        <td className="p-2">{row.platform}</td>
                        <td className="p-2">{row.adFormat}</td>
                        <td className="p-2">{row.productPrefix}</td>
                        <td className="p-2">{row.adType}</td>
                        <td className="p-2">{row.placement}</td>
                        <td className="p-2">{row.country || '-'}</td>
                        <td className="p-2">{row.pamVariant}</td>
                        <td className="p-2">{(row.waterfallPrices || []).join(', ') || '-'}</td>
                        <td className="p-2 break-words whitespace-pre-wrap">
                          {formatBiddingConfig(row)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedData.length > 10 && (
                  <div className="text-sm text-gray-500 mt-2 text-center">
                    ... 还有 {parsedData.length - 10} 行数据
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>生成名称预览</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">序号</th>
                      <th className="text-left p-2">Ad Unit 名称</th>
                      <th className="text-left p-2">中介组名称</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.slice(0, 10).map((row, idx) => {
                      const { adUnitName, mediationGroupName } = generateNamePreview(row)
                      return (
                        <tr key={idx} className="border-b">
                          <td className="p-2">{idx + 1}</td>
                          <td className="p-2 font-mono text-xs">{adUnitName}</td>
                          <td className="p-2 font-mono text-xs">{mediationGroupName}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                {parsedData.length > 10 && (
                  <div className="text-sm text-gray-500 mt-2 text-center">
                    ... 还有 {parsedData.length - 10} 行数据
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              onClick={handleCreate}
              disabled={!canCreate || isLoading}
              size="lg"
            >
              {isLoading ? '创建中...' : '开始创建'}
            </Button>
          </div>
        </>
      )}

      {/* 二次确认对话框 */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-[75vw] max-w-5xl">
            <CardHeader>
              <CardTitle>确认创建</CardTitle>
              <CardDescription>
                即将创建/复用以下资源
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertDescription>
                  <div className="font-semibold mb-2">即将创建/复用资源：</div>
                  <div className="text-sm">
                    Ad Unit: {parsedData.length} 个<br />
                    中介组: {parsedData.length} 个<br />
                    Waterfall 价格配置
                  </div>
                </AlertDescription>
              </Alert>

              <div className="rounded-lg border p-3">
                <div className="text-sm font-semibold mb-2">详细创建信息</div>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">序号</th>
                        <th className="text-left p-2">Ad Unit</th>
                        <th className="text-left p-2">中介组</th>
                        <th className="text-left p-2">Waterfall 价格</th>
                        <th className="text-left p-2">Bidding 配置</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.map((row, idx) => {
                        const { adUnitName, mediationGroupName } =
                          generateNamePreview(row)
                        return (
                          <tr key={idx} className="border-b">
                            <td className="p-2">{idx + 1}</td>
                            <td className="p-2 font-mono">{adUnitName}</td>
                            <td className="p-2 font-mono">{mediationGroupName}</td>
                            <td className="p-2">
                              {(row.waterfallPrices || []).join(', ') || '-'}
                            </td>
                            <td className="p-2 break-words whitespace-pre-wrap">
                              {formatBiddingConfig(row)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {!result && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                  <h3 className="font-semibold text-red-900">
                    ⚠️ 需要验证
                  </h3>
                  <p className="text-sm text-red-800">
                    为防止误操作，请输入以下验证码：
                  </p>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <div className="bg-white border-2 border-red-300 rounded-lg px-4 py-3 text-center">
                      <p className="text-3xl font-bold text-red-900 tracking-wider">
                        {verificationCode}
                      </p>
                    </div>
                    <input
                      type="text"
                      maxLength={6}
                      value={inputCode}
                      onChange={(e) => {
                        setInputCode(e.target.value)
                        setVerifyError('')
                      }}
                      placeholder="输入 6 位验证码"
                      className="h-12 w-full rounded border-2 px-3 text-center text-2xl font-bold tracking-wider sm:flex-1"
                    />
                  </div>
                  {verifyError && (
                    <Alert variant="destructive">
                      <AlertDescription>{verifyError}</AlertDescription>
                    </Alert>
                  )}
                </div>
              )}

              {isLoading && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                  正在创建中，请稍候...
                </div>
              )}

              {result && (
                <div className="space-y-3">
                  <div className="flex gap-4">
                    <Badge variant="default">成功: {result.successCount || 0}</Badge>
                    <Badge variant="secondary">复用: {result.reuseCount || 0}</Badge>
                    <Badge variant="destructive">失败: {result.failCount || 0}</Badge>
                  </div>
                  {result.results && result.results.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold">详细日志:</h4>
                      <div className="max-h-80 overflow-y-auto space-y-1">
                        {result.results.map((r, idx) => (
                          <div
                            key={idx}
                            className={`text-sm p-2 rounded ${
                              r.success ? 'bg-green-50' : 'bg-red-50'
                            }`}
                          >
                            第{r.rowIndex}行: {r.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
            <div className="flex justify-end gap-2 p-6 pt-0">
              {!result && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirmDialog(false)}
                    disabled={isLoading}
                  >
                    取消
                  </Button>
                  <Button
                    onClick={handleConfirmCreate}
                    disabled={inputCode.length !== 6 || isLoading}
                  >
                    {isLoading ? '创建中...' : '确认并提交'}
                  </Button>
                </>
              )}
              {result && (
                <Button
                  onClick={() => setShowConfirmDialog(false)}
                >
                  完成
                </Button>
              )}
            </div>
          </Card>
        </div>
      )}

    </div>
  )
}

export default PamCreatePage
